import React, { useEffect, useState } from "react";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../../Layout/GRISidebar";
import styles from "../../../../styles/gri/general.module.css";
import InputField from "../../../../Components/Masters/InputField";
import { Box, Button, Typography } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { get, put } from "../../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import { validateNumberInput } from "../../../../utils/validateFields";
import MessagePopup from "../../../../Components/Masters/MessagePopup";
import { restructureData } from "../../../../utils/restructureData";
import DynamicStaticTable from "../../../../Components/Common/DynamicStaticTable";
import { validateLastRow } from "../../../../utils/validateTable";
import ReviewBox from "../../../../Components/Common/ReviewBox";

import { currentYear } from "../../../../utils/generateYearRanges";

const GRI305_3 = ({ hide }) => {
  const [griReview, setGriReview] = useState(() =>
    JSON.parse(localStorage.getItem("griReview")),
  );
  const inputData = [
    {
      fieldName: "gasesIncluded",
      // title: "b. Gases included in the calculation:",
      type: "array",
      letter: "b.",
      fields: [
        {
          fieldName: "gasName",
          title: "Gas Name",
          type: "text",
          placeholder: "Enter Text",
          width: "44%",
        },
        // {
        //   fieldName: "unit",
        //   title: "Unit",
        //   type: "select",
        //   options: ["Tonns", "Liters", "M3", "Kg"],
        //   width: "30%",
        //   placeholder: "Select",
        // },
        {
          fieldName: "total",
          title: "Total Emission",
          type: "number",
          width: "44%",
          placeholder: "Enter Number",
        },
      ],
    },
  ];

  const defaultSegments = ["CO2", "CH4", "N2O", "HFCs", "PFCs", "SF6", "NF3"];

  const questionData = [
    {
      fieldName: "scope3EmissionsTotal",
      title:
        "a. What is the total amount of greenhouse gas emissions in metric tons of CO2 equivalent emitted directly (Scope 3 emissions) by the organization?",
      type: "number",
      letter: "a.",
    },
    {
      fieldName: "biogenicCO2Emissions",
      title:
        "c. What is the biogenic CO2 emissions in metric tons of CO2 equivalent?",
      type: "number",
      letter: "c.",
    },
    {
      fieldName: "baseYearEmissionData",
      title: "d. If applicable, define base year for the calculation:",
      type: "text",
      letter: "d.",
      fields: [
        {
          fieldName: "rationaleBehind",
          title: "i. The rationale behind the selection of the base year.",
          type: "text",
        },
        {
          fieldName: "totalEmission",
          title: "ii. The total emissions recorded in the base year.",
          type: "number",
        },
        {
          fieldName: "recalculationBaseYear",
          title:
            "iii. Provide context for any significant changes in emissions that prompted the recalculation of base year emissions.",
          type: "text",
        },
      ],
    },
    {
      fieldName: "emissionFactorsGWPReferences",
      title:
        "e. What is the source of the emission factors and global warming potential (GWP) rates used in emissions calculations? Provide references to the source if available.",
      type: "text",
      letter: "e.",
    },
    {
      fieldName: "emissionsConsolidationMethod",
      title:
        "f. How are emissions consolidated? Specify whether equity share, financial control, or operational control is utilized for consolidation purposes.",
      type: "text",
      letter: "f.",
    },
    {
      fieldName: "emissionsCalculationMethods",
      title:
        "g. What standards, methodologies, assumptions, and/or calculation tools were utilized in emissions calculations?",
      type: "text",
      letter: "g.",
    },
  ];

  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [tables, setTables] = useState([]);
  const [tabErrors, setTabErros] = useState({});
  const [data, setData] = useState({});
  const [formData, setFormData] = useState({});
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await get(`/gri/get?Id=${id}&type=GRI300`);
      if (
        res?.data?.data?.year ? res?.data?.data?.year < currentYear - 1 : false
      )
        setDisabled(true);
      const res1 = res?.data?.data?.GRI300?.scope3Emissions;
      setData(res1);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    setFormData({
      scope3EmissionsTotal:
        data?.scope3EmissionsTotal != null
          ? Number(data.scope3EmissionsTotal).toFixed(3)
          : "",
      biogenicCO2Emissions: data?.biogenicCO2Emissions || "",
      baseYearEmissionData: data?.baseYearEmissionData || {
        rationaleBehind: "",
        totalEmission: "",
        recalculationBaseYear: "",
      },
      emissionFactorsGWPReferences: data?.emissionFactorsGWPReferences || "",
      emissionsConsolidationMethod: data?.emissionsConsolidationMethod || "",
      emissionsCalculationMethods: data?.emissionsCalculationMethods || "",
    });
  }, [data]);

  const handleChange = (fieldName, value, type, field) => {
    if (type === "number" && !validateNumberInput(value)) return;

    field
      ? setFormData((prev) => ({
          ...prev,
          [fieldName]: { ...prev?.[fieldName], [field]: value },
        }))
      : setFormData((prev) => ({
          ...prev,
          [fieldName]: value,
        }));
  };

  const handleSubmit = async () => {
    if (!disabled) {
      if (
        tables?.[0]?.rows?.length > defaultSegments?.length &&
        !validateLastRow(inputData, tables, setTabErros)
      )
        return;
      let transformedData2 = {
        scope3Emissions: { ...restructureData(tables), ...formData },
        type: "GRI300",
        submitted305_3: true,
      };

      try {
        const res = await put(`/gri/update?Id=${id}`, transformedData2);
        setsnackOpen({
          open: true,
          message: res.data.message,
          severity: "success",
        });
        navigate(`/gri/gri300/305-4/${id}`);
      } catch (error) {
        setsnackOpen({
          open: true,
          message: error.data.message,
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri300/305-4/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      <MessagePopup {...{ snackOpen, setsnackOpen }} />{" "}
      {!hide && <p className={styles.section_heading}>305 : Emissions</p>}
      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-2rem" : "1rem" }}
      >
        305-3 : OTHER indirect (Scope 3) GHG emissions{" "}
      </p>
      {questionData.map((el) => (
        <Box key={el.fieldName} sx={{ mt: hide ? "-1rem" : "1rem" }}>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {/* <p className={styles.ques_heading}>{el.letter}</p> */}
            <p className={styles.ques_heading}>{el.title}</p>
          </Box>
          {el?.fields ? (
            <Box sx={{ mt: "-1rem" }}>
              {el.fields.map((field, index) => (
                <Box sx={{ ml: 3 }} key={index}>
                  <Typography sx={{ my: 2, fontWeight: 500, fontSize: "15px" }}>
                    {field?.title}
                  </Typography>
                  <Box sx={{ marginLeft: "1rem", width: "98%" }}>
                    <InputField
                      placeholder={
                        field?.type === "number" ? "Enter Number" : "Enter Text"
                      }
                      fieldName={field?.fieldName}
                      value={formData?.[el?.fieldName]?.[field.fieldName]}
                      handleChange={(e) => {
                        handleChange(
                          el?.fieldName,
                          e.target.value,
                          field?.type,
                          field?.fieldName,
                        );
                      }}
                      mt="-5px"
                      disabled={disabled}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ ml: 2 }}>
              <InputField
                placeholder={
                  el?.type === "number" ? "Enter Number" : "Enter Text"
                }
                fieldName={el.fieldName}
                value={formData?.[el.fieldName]}
                handleChange={(e) => {
                  handleChange(el?.fieldName, e.target.value, el?.type);
                }}
                mt="-5px"
                errors={Boolean(errors[el.fieldName])}
                disabled={disabled}
              />
            </Box>
          )}
          {el.letter === "a." && (
            <Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <p className={styles.ques_heading}>
                  b. Gases included in the calculation:
                </p>
                <p className={styles.ques_heading}>Tons</p>
              </Box>
              <DynamicStaticTable
                {...{
                  inputData,
                  tables,
                  setTables,
                  errors: tabErrors,
                  setErrors: setTabErros,
                  data: data,
                  defaultSegments,
                  parameter: "gasName",
                  buttonHeading: "Add Other Gases",
                  disabled: disabled,
                  hide: hide,
                }}
              />
            </Box>
          )}
        </Box>
      ))}
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98.2%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI305_3;
